import { getCreate2Address } from '@ethersproject/address'
import { pack, keccak256 } from '@ethersproject/solidity'
import { ChainId, Currency, ETHER, Token, WETH } from '@uniswap/sdk'

const OFFICIAL_CHAIN_IDS = [ChainId.MAINNET, ChainId.ROPSTEN, ChainId.RINKEBY, ChainId.GÖRLI, ChainId.KOVAN]
const DEFAULT_V2_ROUTER_ADDRESS = '0x7a250d5630B4cF539739dF2C5dAcb4c659F2488D'
const DEFAULT_V2_FACTORY_ADDRESS = '0x5C69bEe701ef814a2B6a3EDD4B1652CB9cc5aA6f'
const DEFAULT_V2_PAIR_INIT_CODE_HASH = '0x0090a740ed177cac54df91f461a148c085f3483c351ed9ceeafc6f1469114c42'

function readEnv(name: string): string | undefined {
  const value = process.env[name]
  return value && value.trim().length > 0 ? value.trim() : undefined
}

export const NETWORK_CHAIN_ID = parseInt(process.env.REACT_APP_CHAIN_ID ?? '1')
export const NETWORK_NAME = process.env.REACT_APP_NETWORK_NAME ?? 'Custom Network'
export const NETWORK_SHORT_NAME = process.env.REACT_APP_NETWORK_SHORT_NAME ?? NETWORK_NAME
export const NATIVE_CURRENCY_NAME = process.env.REACT_APP_NATIVE_CURRENCY_NAME ?? 'Ether'
export const NATIVE_CURRENCY_SYMBOL = process.env.REACT_APP_NATIVE_CURRENCY_SYMBOL ?? 'ETH'
export const WRAPPED_NATIVE_CURRENCY_SYMBOL =
  process.env.REACT_APP_WRAPPED_NATIVE_CURRENCY_SYMBOL ?? `W${NATIVE_CURRENCY_SYMBOL}`
export const WRAPPED_NATIVE_CURRENCY_NAME =
  process.env.REACT_APP_WRAPPED_NATIVE_CURRENCY_NAME ?? `Wrapped ${NATIVE_CURRENCY_NAME}`
export const BLOCK_EXPLORER_NAME = process.env.REACT_APP_BLOCK_EXPLORER_NAME ?? 'Explorer'
export const BLOCK_EXPLORER_URL = (readEnv('REACT_APP_BLOCK_EXPLORER_URL') ?? '').replace(/\/$/, '')
export const TOKEN_LIST_URL = readEnv('REACT_APP_TOKEN_LIST_URL')

const configuredWrappedNativeAddress = readEnv('REACT_APP_WRAPPED_NATIVE_CURRENCY_ADDRESS')

export const ROUTER_ADDRESS =
  readEnv('REACT_APP_ROUTER_ADDRESS') ??
  (OFFICIAL_CHAIN_IDS.includes(NETWORK_CHAIN_ID as ChainId) ? DEFAULT_V2_ROUTER_ADDRESS : '')

export const FACTORY_ADDRESS =
  readEnv('REACT_APP_FACTORY_ADDRESS') ??
  (OFFICIAL_CHAIN_IDS.includes(NETWORK_CHAIN_ID as ChainId) ? DEFAULT_V2_FACTORY_ADDRESS : '')

export const MULTICALL_ADDRESS = readEnv('REACT_APP_MULTICALL_ADDRESS')
export const V2_PAIR_INIT_CODE_HASH = readEnv('REACT_APP_V2_PAIR_INIT_CODE_HASH') ?? DEFAULT_V2_PAIR_INIT_CODE_HASH

export const CUSTOM_WRAPPED_NATIVE_CURRENCY =
  configuredWrappedNativeAddress &&
  new Token(
    NETWORK_CHAIN_ID as ChainId,
    configuredWrappedNativeAddress,
    18,
    WRAPPED_NATIVE_CURRENCY_SYMBOL,
    WRAPPED_NATIVE_CURRENCY_NAME
  )

export function getWrappedNativeCurrency(chainId?: number): Token | undefined {
  if (!chainId) return undefined
  if (chainId === NETWORK_CHAIN_ID && CUSTOM_WRAPPED_NATIVE_CURRENCY) return CUSTOM_WRAPPED_NATIVE_CURRENCY
  return (WETH as { [chainId: number]: Token | undefined })[chainId]
}

export function computeV2PairAddress(tokenA: Token, tokenB: Token): string {
  const [token0, token1] = tokenA.sortsBefore(tokenB) ? [tokenA, tokenB] : [tokenB, tokenA]
  return getCreate2Address(
    FACTORY_ADDRESS,
    keccak256(['bytes'], [pack(['address', 'address'], [token0.address, token1.address])]),
    V2_PAIR_INIT_CODE_HASH
  )
}

export function isNativeCurrencySymbol(symbol?: string): boolean {
  if (!symbol) return false
  const upper = symbol.toUpperCase()
  return upper === 'ETH' || upper === NATIVE_CURRENCY_SYMBOL.toUpperCase()
}

export function getCurrencyDisplaySymbol(currency?: Currency): string | undefined {
  if (!currency) return undefined
  if (currency === ETHER) return NATIVE_CURRENCY_SYMBOL
  return currency.symbol
}

export function getCurrencyDisplayName(currency?: Currency): string | undefined {
  if (!currency) return undefined
  if (currency === ETHER) return NATIVE_CURRENCY_NAME
  return currency.name
}

export function getChainLabel(chainId?: number): string | null {
  if (!chainId) return null
  if (chainId === NETWORK_CHAIN_ID && !OFFICIAL_CHAIN_IDS.includes(chainId as ChainId)) {
    return NETWORK_SHORT_NAME
  }

  switch (chainId) {
    case ChainId.MAINNET:
      return null
    case ChainId.RINKEBY:
      return 'Rinkeby'
    case ChainId.ROPSTEN:
      return 'Ropsten'
    case ChainId.GÖRLI:
      return 'Görli'
    case ChainId.KOVAN:
      return 'Kovan'
    default:
      return `Chain ${chainId}`
  }
}

export function patchSdkForCustomNetwork(): void {
  if (CUSTOM_WRAPPED_NATIVE_CURRENCY) {
    ;(WETH as { [chainId: number]: Token | undefined })[NETWORK_CHAIN_ID] = CUSTOM_WRAPPED_NATIVE_CURRENCY
  }
}
